import { and, eq, inArray } from "drizzle-orm";
import { db } from "../db/index";
import { users } from "../db/schema/identity";
import { roleUser, permissionRole, userPermissions, permissions, roles } from "../db/schema/rbac";
import { cacheDelete, cacheGetJSON, cacheSetJSON } from "../lib/cache";
import { combineScopes, SCOPE, type ScopeId } from "./scopes";

/** permissionName → effective scope, for one user. */
export type EffectivePermissions = Record<string, ScopeId>;

const key = (userId: number) => `eff_perm:${userId}`;

/**
 * Faithful to legacy (reskin-docs/03; RolePermissionController): the runtime authority is the
 * MATERIALIZED `user_permissions` table (one row per permission per user). For non-customised
 * users it equals the role-derived set; for `customised_permissions=1` users it is their manual
 * set and is preserved. `getEffectivePermissions` reads that table; `syncUserPermissions`
 * rebuilds it from roles (the permission-sync cron + role-scope-change propagation call it).
 */

/** Role-derived scoped set {permissionId → most-permissive scope across the user's roles}. */
async function deriveFromRoles(userId: number): Promise<Map<number, ScopeId>> {
  const roles = await db
    .select({ roleId: roleUser.roleId })
    .from(roleUser)
    .where(eq(roleUser.userId, userId));
  const roleIds = roles.map((r) => r.roleId);
  const scopesByPerm = new Map<number, ScopeId[]>();
  if (roleIds.length) {
    const rows = await db
      .select({ pid: permissionRole.permissionId, scope: permissionRole.permissionTypeId })
      .from(permissionRole)
      .where(inArray(permissionRole.roleId, roleIds));
    for (const r of rows) {
      const a = scopesByPerm.get(r.pid) ?? [];
      a.push(r.scope as ScopeId);
      scopesByPerm.set(r.pid, a);
    }
  }
  const out = new Map<number, ScopeId>();
  for (const [pid, scopes] of scopesByPerm) out.set(pid, combineScopes(scopes));
  return out;
}

/**
 * Rebuild a user's materialized `user_permissions` from their roles, then mark
 * `permission_sync = 1`. Customised users are NOT rebuilt (their manual set is preserved). Always
 * invalidates the cache. Chunked inserts (CloudLinux limits — reskin-docs/01 §6).
 */
export async function syncUserPermissions(userId: number): Promise<void> {
  const u = (
    await db
      .select({ customised: users.customisedPermissions })
      .from(users)
      .where(eq(users.id, userId))
      .limit(1)
  )[0];
  if (!u) return;

  if (!u.customised) {
    const derived = await deriveFromRoles(userId);
    const rows = [...derived.entries()].map(([pid, scope]) => ({
      userId,
      permissionId: pid,
      permissionTypeId: scope,
    }));
    await db.transaction(async (tx) => {
      await tx.delete(userPermissions).where(eq(userPermissions.userId, userId));
      for (let i = 0; i < rows.length; i += 200) {
        await tx.insert(userPermissions).values(rows.slice(i, i + 200));
      }
    });
  }
  await db.update(users).set({ permissionSync: true }).where(eq(users.id, userId));
  await cacheDelete(key(userId));
}

/** Mark a user's effective set stale (call on role assign/remove, override, or role-scope change). */
export async function markPermissionsStale(userId: number): Promise<void> {
  await db.update(users).set({ permissionSync: false }).where(eq(users.id, userId));
  await cacheDelete(key(userId));
}

/** Effective permissions for a user (Memcached-cached). Self-heals: syncs if stale or unmaterialized. */
export async function getEffectivePermissions(userId: number): Promise<EffectivePermissions> {
  const cached = await cacheGetJSON<EffectivePermissions>(key(userId));
  if (cached) return cached;

  const u = (
    await db
      .select({ sync: users.permissionSync })
      .from(users)
      .where(eq(users.id, userId))
      .limit(1)
  )[0];
  if (u && u.sync === false) await syncUserPermissions(userId);

  let rows = await readMaterialized(userId);
  // self-heal: materialized set empty but the user has roles → sync once and re-read
  if (rows.length === 0) {
    const hasRole = (
      await db.select({ rid: roleUser.roleId }).from(roleUser).where(eq(roleUser.userId, userId)).limit(1)
    ).length;
    if (hasRole) {
      await syncUserPermissions(userId);
      rows = await readMaterialized(userId);
    }
  }

  const eff: EffectivePermissions = {};
  for (const r of rows) eff[r.name] = r.scope as ScopeId;
  await cacheSetJSON(key(userId), eff, 3600);
  return eff;
}

async function readMaterialized(userId: number) {
  return db
    .select({ name: permissions.name, scope: userPermissions.permissionTypeId })
    .from(userPermissions)
    .innerJoin(permissions, eq(permissions.id, userPermissions.permissionId))
    .where(eq(userPermissions.userId, userId));
}

/** One permission's effective scope (NONE if absent). */
export async function scopeFor(userId: number, permission: string): Promise<ScopeId> {
  const eff = await getEffectivePermissions(userId);
  return eff[permission] ?? SCOPE.NONE;
}

/**
 * Effective scoped permissions for a ROLE (not a user) — used by the admin "Viewing as" preview
 * (legacy ChromeController@viewAs) to render the nav/dashboard as a given role would see them.
 * Admin role = all-ALL; others = combineScopes over the role's permission_role rows.
 */
export async function getRoleEffectivePermissions(
  companyId: number,
  roleName: string,
): Promise<EffectivePermissions> {
  const [role] = await db
    .select({ id: roles.id, name: roles.name })
    .from(roles)
    .where(and(eq(roles.companyId, companyId), eq(roles.name, roleName)))
    .limit(1);
  if (!role) return {};
  if (role.name === "admin") {
    const all = await db.select({ name: permissions.name }).from(permissions);
    const eff: EffectivePermissions = {};
    for (const p of all) eff[p.name] = SCOPE.ALL;
    return eff;
  }
  const rows = await db
    .select({ name: permissions.name, scope: permissionRole.permissionTypeId })
    .from(permissionRole)
    .innerJoin(permissions, eq(permissions.id, permissionRole.permissionId))
    .where(eq(permissionRole.roleId, role.id));
  const byName = new Map<string, ScopeId[]>();
  for (const r of rows) {
    const a = byName.get(r.name) ?? [];
    a.push(r.scope as ScopeId);
    byName.set(r.name, a);
  }
  const eff: EffectivePermissions = {};
  for (const [n, scopes] of byName) eff[n] = combineScopes(scopes);
  return eff;
}
